import { AppWindowLater } from "./AppReactPoll";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppAssistLogClick, AppEventTarget } from "./AppEventTarget";
import { AppMediaQuery } from "./AppMediaQuery";
import { AppAskAllLink, AppAskAllRules } from "./AppAskAllRules";
import { AppAssistTurn } from "./AppAssistApi";
import { AppAssistDone, AppAssistPanel, AppAssistPiece, AppAssistWho, IAssistDoc, IAssistHost, IAssistMode,
    IAssistNode, IAssistTools } from "./AppAssistPanel";
import { AppAssistSession } from "./AppAssistRules";
import { AppAssistTools } from "./AppAssistTools";
import { AppAssistView } from "./AppAssistView";
import { AppComposer, IComposerBox, IComposerForm, IComposerSent } from "./AppComposer";
import { AppContact } from "./AppIndex";

// AppAskAll - "Ask about everyone" on the main screen (owner-approved #6, 2026-09-27): the ✦ button right under
// the search box opens a panel that asks ONE question across all the owner's one-to-one chats. The rules and the
// markup are `AppAskAllRules`; the log, the rendering (Markdown as elements, tables and charts built by the shared
// allow-list) and the stream are the SAME `AppAssistPanel` as "ask AI about this chat", given this mode.
//
// ⛔ OWNER ONLY: drawn only when `/folder` said `owner: true` and a service is configured; the service proves the
// owner again (`AssistRun`), so a page that drew it anyway would only hear the neutral sentence.
// ⛔ NOTHING SENDS FROM HERE: the mode never draws a draft (`IAssistMode`), and this host's `Send` refuses.
// ⭐ NAMES LINK TO THEIR CHATS - only rows of the page's own index (`AppAskAllRules.Pieces`).
// ⚠ NO SAVED HISTORY (decided 2026-09-27): the answers are derived from the whole archive and go stale as tasks
// close; a root-level history file would need its own push/prune/restore discipline. The session lives for the
// visit (follow-ups carry the earlier turns) and survives every repaint of the main screen.
// ⚠ PRIVATE DATA: no question or answer is ever written to the console.

/** What this box needs from the page: the two `/assist` transports. `AppMain` has both. */
export interface IAskAllPage {
    AssistCitation?(ChatId: string, MessageId: string): void;
    AssistPost(Body: string, Done: AppAssistDone): void;
    AssistPostStream(Body: string, OnText: (Partial: string) => void, Done: AppAssistDone): void;
}

/**
 * The Ask box of an AI panel - the contact's (`AppMain`) and "Ask about everyone" (`AppAskAll`) - wired and submitted
 * the same way. ⛔ The box's composer has NO sender (null): Enter asks, it can never reach `/send`.
 * Each caller passes its own ids and its own catch label (`Where`).
 */
export class AppAskForm {
    /** The form's submit listener and the box's grow-and-Enter behaviour; nothing when either element is missing. */
    public static Wire = (FormId: string, BoxId: string, OnSubmit: (Ev: Event) => void, Coarse: boolean, Where: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Form: HTMLElement | null = document.getElementById(FormId);
            const Box: HTMLElement | null = document.getElementById(BoxId);
            if (Form === null || Box === null) { return; }
            Form.addEventListener("submit", OnSubmit);
            new AppComposer(Box as unknown as IComposerBox, Form as unknown as IComposerForm, Coarse, null).Wire();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, Where);
        }
    };

    /** A submit: the box's words to `Panel.Ask`, and the box emptied (and shrunk) only when the panel took them. */
    public static Submit = (Ev: Event, BoxId: string, Panel: AppAssistPanel | null, Where: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Ev.preventDefault();
            const Box: HTMLTextAreaElement | null = document.getElementById(BoxId) as HTMLTextAreaElement | null;
            if (Box === null || Panel === null) { return; }
            if (Panel.Ask(Box.value) === true) {
                Box.value = "";
                Box.style.height = "";
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, Where);
        }
    };
}

export class AppAskAll implements IAssistMode {
    /** A warm-up at most this often from this box (the service throttles too). */
    public static readonly WarmEveryMs: number = 60000;

    private Page: IAskAllPage;
    private Session: AppAssistSession = new AppAssistSession();
    private Panel: AppAssistPanel | null = null;
    private Phone: string = "";
    private Key: string = "";
    private Links: AppAskAllLink[] = [];
    private Allowed: boolean = false;
    private Open: boolean = false;
    private LastWarm: number = 0;

    /** The index's money rows carry the new fields - the "Revenue" chip is shown. */
    private Money: boolean = false;

    public constructor(Page: IAskAllPage) {
        this.Page = Page;
    }

    /**
     * The box's markup for this paint, or "" when this browser may not ask. ⚠ Another number (a sign-out, another
     * subscriber on a shared browser) starts a fresh session - one account's answers never show for another.
     */
    public Html = (Allowed: boolean, Phone: string, Key: string, Contacts: AppContact[], Money: boolean = false): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⭐ THE "REVENUE" CHIP (2026-10-14): only on an index whose money rows carry the new fields (`WAC_MONEY`).
            this.Money = Money === true;
            if (nz(Phone) !== this.Phone) { this.Session = new AppAssistSession(); this.Open = false; }
            this.Allowed = Allowed === true && nz(Phone).length > 0 && nz(Key).length > 0;
            this.Phone = nz(Phone);
            this.Key = nz(Key);
            this.Links = AppAskAll.LinksOf(Contacts);
            if (this.Allowed !== true) { this.Panel = null; return ""; }
            return AppAskAllRules.Html(this.Open, this.Money);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.Html");
            return "";
        }
    };

    /** The page's contacts as links - one-to-one rows only (a group is never asked about or linked). */
    public static LinksOf = (Contacts: AppContact[]): AppAskAllLink[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppAskAllLink[] = [];
            for (const One of Contacts) {
                if (One.IsGroup === true || nz(One.Href).length === 0) { continue; }
                const Link: AppAskAllLink = new AppAskAllLink();
                Link.Href = One.Href;
                Link.Title = One.Title;
                Out.push(Link);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.LinksOf");
            return [];
        }
    };

    /** After the paint: the panel over the new log (same session), and every control wired. */
    public Wire = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Panel = null;
            const Log: HTMLElement | null = document.getElementById(AppAskAllRules.LogId);
            if (this.Allowed !== true || Log === null) { return; }
            this.Attach(document as unknown as IAssistDoc, Log as unknown as IAssistNode, new AppAssistTools());
            this.WireControls();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.Wire");
        }
    };

    /**
     * This box's panel over `Log` (same session, this mode, this host), drawn - or null when the browser may not ask.
     * ⚠ DOM-free (the document and the tools are passed in), so the suite drives the real panel.
     */
    public Attach = (Doc: IAssistDoc, Log: IAssistNode, Tools: IAssistTools | null): AppAssistPanel | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            this.Panel = null;
            if (this.Allowed !== true) { return null; }
            const Who: AppAssistWho = new AppAssistWho();
            Who.Phone = this.Phone;
            Who.Key = this.Key;
            const Made: AppAssistPanel = new AppAssistPanel(Doc, Log, new AppAskAllHost(this), this.Session, Who, "", Tools);
            Made.Mode = this;
            this.Panel = Made;
            Made.Render();
            return Made;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.Attach");
            return null;
        }
    };

    private WireControls = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppEventTarget.OnClick(AppAskAllRules.OpenId, this.OnToggle, "AppAskAll.On");
            AppEventTarget.OnClick(AppAskAllRules.CloseId, this.OnClose, "AppAskAll.On");
            AppEventTarget.OnClick(AppAskAllRules.ChipsId, this.OnChip, "AppAskAll.On");
            AppEventTarget.OnClick(AppAskAllRules.LogId, this.OnLogClick, "AppAskAll.On");
            // ⚠ THE PANEL'S GROW-AND-ENTER BEHAVIOUR; ⛔ with NO sender (null) - this box cannot reach `/send`.
            AppAskForm.Wire(AppAskAllRules.FormId, AppAskAllRules.BoxId, this.OnSubmit, AppAskAll.Coarse(), "AppAskAll.WireControls");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.WireControls");
        }
    };

    private static Coarse = (): boolean => {
        return AppMediaQuery.IsCoarse("AppAskAll.Coarse");
    };

    /** Show or hide the panel in place - the list around it is not repainted. */
    private Show = (Open: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Open = Open;
            const Panel: HTMLElement | null = document.getElementById(AppAskAllRules.PanelId);
            const Button: HTMLElement | null = document.getElementById(AppAskAllRules.OpenId);
            if (Panel !== null) { Panel.hidden = Open !== true; }
            if (Button !== null) { Button.setAttribute("aria-expanded", String(Open === true)); }
            if (Open !== true) { return; }
            if (this.Panel !== null) { (this.Panel as AppAssistPanel).Render(); }
            this.Warm();
            const Box: HTMLElement | null = document.getElementById(AppAskAllRules.BoxId);
            if (Box !== null && AppAskAll.Coarse() !== true) { Box.focus(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.Show");
        }
    };

    private OnToggle = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Show(this.Open !== true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.OnToggle");
        }
    };

    private OnClose = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Show(false);
            const Button: HTMLElement | null = document.getElementById(AppAskAllRules.OpenId);
            if (Button !== null) { Button.focus(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.OnClose");
        }
    };

    /** ⭐ Opening the panel readies Claude for THIS panel's prompt - at most once a minute from here. */
    private Warm = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Now: number = Date.now();
            if (this.LastWarm > 0 && Now - this.LastWarm < AppAskAll.WarmEveryMs) { return; }
            const Body: string = AppAskAllRules.WarmBody(this.Phone, this.Key);
            if (Body.length === 0) { return; }
            this.LastWarm = Now;
            this.Page.AssistPost(Body, this.OnWarmed);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.Warm");
        }
    };

    /**
     * The warm-up's answer. ERROR-SUPPRESSED-JUSTIFIED: a warm-up only saves time on the next question; whatever
     * it answered (started, busy, off, a lost connection), the question itself says what it needs to.
     */
    private OnWarmed = (Status: number, Text: string): void => {
        return;
    };

    /** A chip: open the panel if needed and ask its question. ⚠ The chip carries an INDEX, never the text. */
    private OnChip = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Chip: Element | null = AppEventTarget.Closest(Ev, "[" + AppAssistView.AskAttr + "]", "AppAskAll.Closest");
            if (Chip === null || this.Panel === null) { return; }
            const At: number = parseInt(nz(Chip.getAttribute(AppAssistView.AskAttr)), 10);
            const Asked: string = AppAskAllRules.QuestionAt(At, this.Money);
            if (Asked.length === 0) { return; }
            (this.Panel as AppAssistPanel).Ask(Asked);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.OnChip");
        }
    };

    /** A button in the log - Try again, Cancel, Copy table, Save chart. ⛔ There is no Send to press. */
    private OnLogClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppAssistLogClick.Route(Ev, "AppAskAll.Closest", this.Panel, this.Page.AssistCitation);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.OnLogClick");
        }
    };

    /** The free question. ⚠ The box is emptied only when the question actually went. */
    private OnSubmit = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppAskForm.Submit(Ev, AppAskAllRules.BoxId, this.Panel, "AppAskAll.OnSubmit");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.OnSubmit");
        }
    };

    /** An answer landed (or the stream grew): draw the log again - the panel may be a later paint's. */
    public Redraw = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Panel !== null) { (this.Panel as AppAssistPanel).Render(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.Redraw");
        }
    };

    public PostBody = (Body: string, Done: AppAssistDone): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Page.AssistPost(Body, Done);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.PostBody");
        }
    };

    public PostStreamBody = (Body: string, OnText: (Partial: string) => void, Done: AppAssistDone): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Page.AssistPostStream(Body, OnText, Done);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.PostStreamBody");
        }
    };

    // ── IAssistMode ──────────────────────────────────────────────────────────────────────────

    public Body = (Question: string, Turns: AppAssistTurn[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppAskAllRules.Body(this.Phone, this.Key, Question, Turns);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.Body");
            return "";
        }
    };

    public Intro = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⭐ NO PARAGRAPH IN THE LOG: the explanation is the button's and heading's tooltip (`AppAskAllRules.Html`),
            // so an empty log takes no room ("" = `AppAssistPanel.Render` draws no intro).
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.Intro");
            return "";
        }
    };

    public Pieces = (Text: string): AppAssistPiece[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            return AppAskAllRules.Pieces(Text, this.Links);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAll.Pieces");
            return [];
        }
    };
}

/**
 * The panel's way back into the page for "Ask about everyone". ⛔ `Send` REFUSES - there is no draft to send and
 * no chat to send it in; `Fill`, `Copy` and `Outcome` have nothing to do; there is no saved history to read.
 */
export class AppAskAllHost implements IAssistHost {
    private LaterPort: AppWindowLater = new AppWindowLater("AppAskAllHost");
    private Owner: AppAskAll;

    public constructor(Owner: AppAskAll) {
        this.Owner = Owner;
    }

    /** ⭐ A refusal that clears on its own is asked again after `Ms` (`IAssistHost.Later`). */
    // REUSE-JUSTIFIED: the timer IS shared - `AppWindowLater.Later`. This is the all-contact panel host's own
    // `IAssistHost.Later` (`AppMain.ts` has three more, one per host class): a clamp and one call, own label.
    public Later =(Ms: number, Then: () => void): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.LaterPort.Later(Math.max(0, Ms), Then);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAllHost.Later");
        }
    };

    public Post = (Body: string, Done: AppAssistDone): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Owner.PostBody(Body, Done);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAllHost.Post");
        }
    };

    public PostStream = (Body: string, OnText: (Partial: string) => void, Done: AppAssistDone): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Owner.PostStreamBody(Body, OnText, Done);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAllHost.PostStream");
        }
    };

    /** ⛔ NEVER A SEND. DOCUMENTED EXCEPTION: constant refusal, cannot throw. */
    public Send = (Text: string, Then: IComposerSent): string => {
        return AppAskAllRules.NoSendWhy();
    };

    /** DOCUMENTED EXCEPTION: nothing to fill - this panel has no composer. */
    public Fill = (Text: string): void => {
        return;
    };

    /** DOCUMENTED EXCEPTION: drafts are the only thing copied through the host, and this panel has none. */
    public Copy = (Text: string): void => {
        return;
    };

    public Answered = (Session: AppAssistSession): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Owner.Redraw();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAllHost.Answered");
        }
    };

    /** DOCUMENTED EXCEPTION: no draft, so no outcome to record. */
    public Outcome = (Id: string, Outcome: string): void => {
        return;
    };

    /** No saved history for this panel (see the file header): the empty one, at once. */
    public ReadHistory = (Done: (Text: string) => void, Missing: () => void, Fail: (Why: string) => void): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Missing();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAllHost.ReadHistory");
        }
    };
}
